import { useMemo, useState, useEffect } from "react";
import NumberFlow from "@number-flow/react";
import { useAnalyticsStore } from "@/stores/creators/analyticsStore";
import { useCreatorsStore } from "@/stores/creators/creatorsStore";
import { motion, AnimatePresence } from "framer-motion";

export interface CreatorAnalytics {
  avatar: string;
  clicks: number;
  leads: number;
  link_id: string;
  sale_amount: number;
  sales: number;
  short_link: string;
  username: string;
}

interface AnalyticsSummaryBarProps {
  data: CreatorAnalytics[];
}

interface MetricHeaderProps {
  label: string;
  value: number;
  suffix?: string;
  format?: "number" | "percent";
  change?: number;
  comparison: string;
}

function MetricHeader({ label, value, suffix, format = "number", change, comparison }: MetricHeaderProps) {
  // Start from 0 and animate to actual value on mount
  const [displayValue, setDisplayValue] = useState(0);
  const [displayChange, setDisplayChange] = useState(0);

  useEffect(() => {
    // Small delay to ensure NumberFlow animates from 0
    const timeout = setTimeout(() => {
      setDisplayValue(value);
      if (change !== undefined) setDisplayChange(change);
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  // Update when value changes (not just on mount)
  useEffect(() => {
    setDisplayValue(value);
    if (change !== undefined) setDisplayChange(change);
  }, [value, change]);

  return (
    <div className="text-left w-full">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        {label}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-2xl font-bold text-foreground">
          {format === "percent" ? (
            <NumberFlow
              value={displayValue}
              suffix="%"
              format={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
              willChange
            />
          ) : (
            <NumberFlow value={displayValue} suffix={suffix} willChange />
          )}
        </span>
        {change !== undefined && (
          <span className="font-medium text-success font-mono">
            <NumberFlow
              value={displayChange}
              format={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
              suffix="%"
              willChange
            />
          </span>
        )}
        <div className="text-xs text-muted-foreground">{comparison}</div>
      </div>
    </div>
  );
}

interface MetricConfig {
  key: string;
  label: string;
  value: number;
  suffix?: string;
  format?: "number" | "percent";
  change?: number;
  comparison: string;
}

export default function AnalyticsSummaryBar({ data }: AnalyticsSummaryBarProps) {
  const { selectedTab } = useAnalyticsStore();
  const { creators } = useCreatorsStore();

  // Filter activated creators for video analytics
  const activatedCreators = useMemo(() => {
    return creators.filter((creator) => creator.status === "activated");
  }, [creators]);

  // Conversion summary
  const conversionSummary = useMemo(() => {
    if (!data?.length) {
      return { totalClicks: 0, totalLeads: 0, totalSales: 0, totalRevenue: 0 };
    }
    return {
      totalClicks: data.reduce((sum, c) => sum + c.clicks, 0),
      totalLeads: data.reduce((sum, c) => sum + c.leads, 0),
      totalSales: data.reduce((sum, c) => sum + c.sales, 0),
      totalRevenue: data.reduce((sum, c) => sum + c.sale_amount, 0),
    };
  }, [data]);

  // Video summary
  const videoSummary = useMemo(() => {
    let totalViews = 0;
    let totalLikes = 0;
    let totalComments = 0;
    let totalShares = 0;

    for (const creator of activatedCreators) {
      if (creator.uploaded_links) {
        for (const link of creator.uploaded_links) {
          totalViews += link.views || 0;
          totalLikes += link.likes || 0;
          totalComments += link.comments || 0;
          totalShares += link.shares || 0;
        }
      }
    }

    return { totalViews, totalLikes, totalComments, totalShares };
  }, [activatedCreators]);

  // Conversion rates
  const leadConversionRate = conversionSummary.totalClicks > 0
    ? (conversionSummary.totalLeads / conversionSummary.totalClicks) * 100
    : 0;

  const saleConversionRate = conversionSummary.totalLeads > 0
    ? (conversionSummary.totalSales / conversionSummary.totalLeads) * 100
    : 0;

  // Video rates
  const engagementRate = videoSummary.totalViews > 0
    ? ((videoSummary.totalLikes + videoSummary.totalComments + videoSummary.totalShares) / videoSummary.totalViews) * 100
    : 0;

  // Compute metrics based on selected tab - array structure for animated transitions
  const metrics: MetricConfig[] = useMemo(() => {
    if (selectedTab === "video") {
      return [
        {
          key: "views",
          label: "VIEWS",
          value: videoSummary.totalViews,
          comparison: "",
        },
        {
          key: "likes",
          label: "LIKES",
          value: videoSummary.totalLikes,
          comparison: "",
        },
        {
          key: "comments",
          label: "COMMENTS",
          value: videoSummary.totalComments,
          comparison: "",
        },
        {
          key: "shares",
          label: "SHARES",
          value: videoSummary.totalShares,
          comparison: "",
        },
        {
          key: "engagement",
          label: "ENG. RATE",
          value: engagementRate,
          format: "percent" as const,
          comparison: "",
        },
      ];
    }
    return [
      {
        key: "clicks",
        label: "CLICKS",
        value: conversionSummary.totalClicks,
        comparison: "Total link clicks",
      },
      {
        key: "leads",
        label: "LEADS",
        value: conversionSummary.totalLeads,
        change: leadConversionRate,
        comparison: "from clicks",
      },
      {
        key: "sales",
        label: "SALES",
        value: conversionSummary.totalSales,
        suffix: ` ($${(conversionSummary.totalRevenue / 100).toFixed(1)})`,
        change: saleConversionRate,
        comparison: "from leads",
      },
    ];
  }, [
    selectedTab,
    videoSummary,
    conversionSummary,
    engagementRate,
    leadConversionRate,
    saleConversionRate,
  ]);

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-4">
        <AnimatePresence mode="popLayout">
          {metrics.map((metric) => (
            <motion.div
              key={metric.key}
              layout
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{
                layout: { duration: 0.3, ease: "easeInOut" },
                opacity: { duration: 0.3 },
                scale: { duration: 0.3 },
              }}
              className="flex-1"
            >
              <MetricHeader {...metric} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
